/**
 * ProjectContext
 * Provides global access to the active project state.
 * Replaces independent useProject instances to ensure UI synchronization.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Project, JenialProjectFile } from '../types/project';
import { SegmentWithUI } from '../types/timeline';
import { getShotsByProject, blobToBase64, getAllCharacters, saveCharacter, getCharacter } from '../services/localMediaDB';
import { uploadLargeFileToDrive, updateLargeFileOnDrive } from '../services/mediaUploadService';

const PROJECTS_KEY = 'studio_jenial_projects';
const ACTIVE_PROJECT_KEY = 'studio_jenial_active_project';
const AUTOSAVE_INTERVAL = 30000;

interface ProjectContextType {
    project: Project | null;
    projects: Project[];
    isDirty: boolean;
    createProject: (name: string, options?: any) => Project;
    openProject: (id: string) => boolean;
    updateProject: (updates: Partial<Project>) => void;
    updateProjectMetadata: (id: string, updates: Partial<Project>) => void;
    saveProject: () => Promise<void>;
    addShotToProject: (shotId: string) => void;
    exportProject: (usePicker?: boolean, timelineSegments?: SegmentWithUI[], promptSequence?: any, dogma?: any) => Promise<void>;
    importProject: (file: File) => Promise<JenialProjectFile | null>;
    deleteProject: (id: string) => void;
    refreshProjects: () => void;
}

const ProjectContext = createContext<ProjectContextType | null>(null);

// === STORAGE HELPERS ===
function getProjectsFromStorage(): Project[] {
    try {
        const data = localStorage.getItem(PROJECTS_KEY);
        return data ? JSON.parse(data) : [];
    } catch {
        return [];
    }
}

function saveProjectsToStorage(projects: Project[]): void {
    localStorage.setItem(PROJECTS_KEY, JSON.stringify(projects));
}

function getActiveProjectId(): string | null {
    return localStorage.getItem(ACTIVE_PROJECT_KEY);
}

function setActiveProjectId(id: string | null): void {
    if (id) localStorage.setItem(ACTIVE_PROJECT_KEY, id);
    else localStorage.removeItem(ACTIVE_PROJECT_KEY);
}

export const ProjectProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [project, setProject] = useState<Project | null>(null);
    const [projects, setProjects] = useState<Project[]>([]);
    const [isDirty, setIsDirty] = useState(false);

    // Initial Load
    useEffect(() => {
        refreshProjects();
    }, []);

    const refreshProjects = useCallback(() => {
        const allProjects = getProjectsFromStorage();
        setProjects(allProjects);
        const activeId = getActiveProjectId();
        if (activeId) {
            const active = allProjects.find(p => p.id === activeId);
            if (active) setProject(active);
        }
    }, []);

    // Auto-save
    useEffect(() => {
        if (!project || !isDirty) return;
        const timer = setInterval(() => {
            if (isDirty) saveCurrentProject(false); // Silent save
        }, AUTOSAVE_INTERVAL);
        return () => clearInterval(timer);
    }, [project, isDirty]);

    const saveCurrentProject = useCallback(async (verbose = true) => {
        if (!project) return;
        const updated: Project = { ...project, updatedAt: new Date().toISOString() };

        const allProjects = getProjectsFromStorage();
        const index = allProjects.findIndex(p => p.id === project.id);
        if (index >= 0) allProjects[index] = updated;
        else allProjects.push(updated);

        saveProjectsToStorage(allProjects);
        setProjects(allProjects);
        setProject(updated);
        setIsDirty(false);
        if (verbose) console.log('[ProjectContext] Saved:', updated.name);

        // Drive Sync (Background)
        // ... (Simplified logic similar to hook)
    }, [project]);

    const createProject = useCallback((name: string, options?: any) => {
        const newProject: Project = {
            id: `proj-${Date.now()}`,
            name,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            fps: 24,
            aspectRatio: '16:9',
            resolution: '1080p',
            shotIds: [],
            trackIds: ['v1', 'a1'],
            playheadSec: 0,
            sequenceMode: 'plan-sequence',
            ...options
        };
        const all = [...getProjectsFromStorage(), newProject];
        saveProjectsToStorage(all);
        setProjects(all);
        setProject(newProject);
        setActiveProjectId(newProject.id);
        setIsDirty(false);
        return newProject;
    }, []);

    const openProject = useCallback((id: string) => {
        const all = getProjectsFromStorage();
        const found = all.find(p => p.id === id);
        if (found) {
            setProject(found);
            setActiveProjectId(id);
            setIsDirty(false);
            console.log('[ProjectContext] Opened:', found.name);
            return true;
        }
        return false;
    }, []);

    const updateProject = useCallback((updates: Partial<Project>) => {
        setProject(prev => {
            if (!prev) return null;
            return { ...prev, ...updates };
        });
        setIsDirty(true);
    }, []);

    // Update any project in the list (metadata only)
    const updateProjectMetadata = useCallback((id: string, updates: Partial<Project>) => {
        const all = getProjectsFromStorage();
        const index = all.findIndex(p => p.id === id);

        if (index >= 0) {
            const updated = { ...all[index], ...updates, updatedAt: new Date().toISOString() };
            all[index] = updated;
            saveProjectsToStorage(all);
            setProjects(all);

            // If this is the active project, update it too
            if (project?.id === id) {
                setProject(prev => prev ? { ...prev, ...updates } : null);
            }
        }
    }, [project]);

    const addShotToProject = useCallback((shotId: string) => {
        setProject(prev => {
            if (!prev) return null;
            if (prev.shotIds.includes(shotId)) return prev;
            return { ...prev, shotIds: [...prev.shotIds, shotId] };
        });
        setIsDirty(true);
    }, []);

    const exportProject = useCallback(async (usePicker = false, timelineSegments?: SegmentWithUI[], promptSequence?: any, dogma?: any) => {
        if (!project) return;
        await saveCurrentProject(false);
        const shots = await getShotsByProject(project.id);

        const exportData: JenialProjectFile = {
            version: '1.0',
            project,
            shots: await Promise.all(shots.map(async (shot) => ({
                id: shot.id,
                prompt: shot.prompt,
                thumbnail: shot.thumbnailBlob ? await blobToBase64(shot.thumbnailBlob) : undefined,
                videoRef: shot.videoUrl || undefined,
            }))),
            characters: await getAllCharacters(),
            segments: timelineSegments?.map(seg => ({
                id: seg.id,
                trackId: seg.trackId,
                inSec: seg.inSec,
                outSec: seg.outSec,
                sourceInSec: seg.sourceInSec,
                label: seg.label,
                mediaId: seg.mediaId,
            })) as any,
            promptSequence,
            dogma
        };

        const json = JSON.stringify(exportData, null, 2);
        const filename = `${project.name.replace(/[^a-z0-9]/gi, '_')}.jenial`;

        if (usePicker && 'showSaveFilePicker' in window) {
            try {
                const handle = await (window as any).showSaveFilePicker({
                    suggestedName: filename,
                    types: [{ description: 'Fichier Jenial', accept: { 'application/json': ['.jenial'] } }],
                });
                const writable = await handle.createWritable();
                await writable.write(json);
                await writable.close();
                return;
            } catch (err: any) {
                console.warn('Picker failed/cancelled', err);
            }
        }

        const blob = new Blob([json], { type: 'application/octet-stream' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, [project, saveCurrentProject]);

    const importProject = useCallback(async (file: File) => {
        try {
            const text = await file.text();
            const data: JenialProjectFile = JSON.parse(text);
            const importedProject: Project = {
                ...data.project,
                id: `proj-${Date.now()}`,
                updatedAt: new Date().toISOString(),
            };
            const all = [...getProjectsFromStorage(), importedProject];
            saveProjectsToStorage(all);
            setProjects(all);
            setProject(importedProject);
            setActiveProjectId(importedProject.id);

            // Import chars logic... (simplified)
            return data;
        } catch (err) {
            console.error(err);
            return null;
        }
    }, []);

    const deleteProject = useCallback((id: string) => {
        const all = getProjectsFromStorage().filter(p => p.id !== id);
        saveProjectsToStorage(all);
        setProjects(all);
        if (project?.id === id) {
            setProject(null);
            setActiveProjectId(null);
        }
    }, [project]);

    return (
        <ProjectContext.Provider value={{
            project,
            projects,
            isDirty,
            createProject,
            openProject,
            updateProject,
            updateProjectMetadata,
            saveProject: () => saveCurrentProject(true),
            addShotToProject,
            exportProject,
            importProject,
            deleteProject,
            refreshProjects
        }}>
            {children}
        </ProjectContext.Provider>
    );
};

export const useProjectContext = () => {
    const context = useContext(ProjectContext);
    if (!context) throw new Error('useProjectContext must be used within ProjectProvider');
    return context;
};
